import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import './SealChatPrivacyPolicy.css';

const SealChatPrivacyPolicy = () => {
    useEffect(() => {
        window.scrollTo(0, 0);
        document.title = "Privacy Policy - SealChat";
    }, []);

    return (
        <div className="privacy-container">
            <div className="privacy-content">
                <Link to="/" className="back-home">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="19" y1="12" x2="5" y2="12"></line>
                        <polyline points="12 19 5 12 12 5"></polyline>
                    </svg>
                    Back to Home
                </Link>

                <header className="privacy-header">
                    <h1>Privacy Policy for SealChat</h1>
                    <p className="privacy-effective-date">Last Updated: April 19, 2026</p>
                </header>

                <section className="privacy-section">
                    <p>
                        At SealChat, we prioritize your privacy and are committed to protecting your personal data. 
                        This Privacy Policy explains how we collect, use, and safeguard your information when you use our application.
                    </p>
                </section>

                <section className="privacy-section">
                    <h2>1. Information We Collect</h2>
                    
                    <h3>A. Personal Information</h3>
                    <ul>
                        <li><strong>Phone Number</strong>: We use your phone number as a unique identifier to allow other users to find and connect with you.</li>
                        <li><strong>Display Name</strong>: A name you provide during setup to identify yourself to your contacts.</li>
                        <li><strong>Presence Status</strong>: Real-time information about whether you are online, offline, or in relay mode.</li>
                    </ul>

                    <h3>B. Message Data</h3>
                    <ul>
                        <li><strong>Peer-to-Peer (P2P)</strong>: When both users are online, messages are sent directly between devices using WebRTC technology. These messages do not pass through our central servers.</li>
                        <li><strong>Cloud Fallback</strong>: If a recipient is offline, messages are temporarily stored in a secure cloud database (Firebase Firestore) until the recipient comes online and the message is delivered.</li>
                    </ul>

                    <h3>C. Technical Data</h3>
                    <ul>
                        <li><strong>Signaling Data</strong>: To establish a direct P2P connection, we use signaling data (ICE candidates, offers, and answers) via Firebase.</li>
                        <li><strong>Device Information</strong>: Basic device information required for the app's functionality and performance monitoring.</li>
                    </ul>
                </section>

                <section className="privacy-section">
                    <h2>2. How We Use Your Information</h2>
                    <p>We use the collected information to:</p>
                    <ul>
                        <li>Facilitate secure, direct communication between users.</li>
                        <li>Verify your identity during the authentication process.</li>
                        <li>Enable user discovery via phone numbers.</li>
                        <li>Improve application performance and stability.</li>
                    </ul>
                </section>

                <section className="privacy-section">
                    <h2>3. Data Storage and Security</h2>
                    
                    <h3>A. Local Storage</h3>
                    <p>
                        SealChat uses <strong>Hive</strong>, an ultra-fast local database, to store your chat history and profile information directly on your device. 
                        This ensures your data remains under your control and is accessible offline.
                    </p>

                    <h3>B. Cloud Services</h3>
                    <p>We use <strong>Firebase (Google Cloud)</strong> for:</p>
                    <ul>
                        <li><strong>Authentication</strong>: Securely managing your login and identity.</li>
                        <li><strong>Signaling</strong>: Facilitating the handshake required for WebRTC P2P connections.</li>
                        <li><strong>Storage Fallback</strong>: Temporary storage for messages when one party is offline.</li>
                    </ul>

                    <h3>C. Security Measures</h3>
                    <p>Our architecture is designed to minimize data exposure:</p>
                    <ul>
                        <li><strong>P2P Communication</strong>: Direct transmission reduces the risk of server-side interception.</li>
                        <li><strong>Secure Infrastructure</strong>: We leverage industry-standard security provided by Firebase and WebRTC protocols.</li>
                    </ul>
                </section>

                <section className="privacy-section">
                    <h2>4. Data Sharing and Disclosure</h2>
                    <p><strong>We do not sell, trade, or rent your personal information to third parties.</strong></p>
                    <p>We may share information only in the following limited circumstances:</p>
                    <ul>
                        <li><strong>Service Providers</strong>: With Google (Firebase) as the infrastructure provider for authentication and signaling.</li>
                        <li><strong>Legal Requirements</strong>: If required by law or in response to valid legal requests by public authorities.</li>
                    </ul>
                </section>

                <section className="privacy-section">
                    <h2>5. Your Rights and Choices</h2>
                    <ul>
                        <li><strong>Access and Update</strong>: You can update your display name within the application settings.</li>
                        <li><strong>Account Deletion</strong>: You may request the deletion of your account and associated data by contacting us.</li>
                        <li><strong>Communication Preferences</strong>: You control who can message you by sharing or withholding your phone number.</li>
                    </ul>
                </section>

                <section className="privacy-section">
                    <h2>6. Children's Privacy</h2>
                    <p>SealChat is not intended for children under the age of 13. We do not knowingly collect personal information from children.</p>
                </section>

                <section className="privacy-section">
                    <h2>7. Safety and Content Standards (CSAE)</h2>
                    <p>
                        SealChat has zero tolerance for Child Sexual Abuse Material (CSAM) and Child Sexual Abuse and Exploitation (CSAE). 
                        We are committed to providing a safe platform and maintaining the following standards:
                    </p>
                    <ul>
                        <li><strong>Prohibited Content</strong>: The creation, sharing, or promotion of CSAM or any form of child exploitation is strictly prohibited.</li>
                        <li><strong>Reporting and Cooperation</strong>: We actively cooperate with global law enforcement agencies and organizations, including the National Center for Missing & Exploited Children (NCMEC), to report illegal content and assist in investigations.</li>
                        <li><strong>Standard Adherence</strong>: We align our safety protocols with industry-standard frameworks for child safety and online protection to ensure our users remain safe.</li>
                    </ul>
                </section>

                <section className="privacy-section">
                    <h2>8. Changes to This Policy</h2>
                    <p>
                        We may update our Privacy Policy from time to time. We will notify you of any changes by posting 
                        the new Privacy Policy on this page and updating the "Last Updated" date.
                    </p>
                </section>

                <section className="privacy-section">
                    <h2>9. Contact Us</h2>
                    <p>If you have any questions about this Privacy Policy, please contact us at:</p>
                    <ul>
                        <li><strong>Albo Labs Support</strong></li>
                        <li>Email: <a href="mailto:privacy@albolabs.com" className="privacy-link">privacy@albolabs.com</a></li>
                        <li>Website: <a href="https://albolabs.com" className="privacy-link" target="_blank" rel="noopener noreferrer">albolabs.com</a></li>
                    </ul>
                </section>

                <footer className="privacy-footer">
                    <p>&copy; {new Date().getFullYear()} SealChat. All rights reserved.</p>
                </footer>
            </div>
        </div>
    );
};

export default SealChatPrivacyPolicy;
